import { clone, cloneDeep } from "lodash";
import proj4 from "proj4";
import { ProjectionsRegistrar } from "./ProjectionRegistrar";

ProjectionsRegistrar.RegisterEPSG27700();
ProjectionsRegistrar.RegisterEastingNorthing();

export class GeometryProjector {
    public static projCoordinatesToWGS84(coordinates: GeoJSON.Position, projFrom = "EPSG:27700") {
        return proj4(projFrom, "WGS84", coordinates);
    }
    public static projectTo27700(geometry: GeoJSON.FeatureCollection): GeoJSON.FeatureCollection {
        const result = clone(geometry);
        for (const feature of result.features) {
            const geometry = feature.geometry as GeoJSON.Geometry;
            if (!geometry) {
                continue;
            }
            if (geometry.type === "MultiPolygon") {
                const multiPolygon = geometry as GeoJSON.MultiPolygon;
                for (const coordItem of multiPolygon.coordinates) {
                    for (let i = 0; i < coordItem.length; i++) {
                        for (let j = 0; j < coordItem[i].length; j++) {
                            coordItem[i][j] = proj4("WGS84", "EPSG:27700", coordItem[i][j]);
                            coordItem[i][j] = [Math.round(coordItem[i][j][0] * 100) / 100, Math.round(coordItem[i][j][1] * 100) / 100];
                        }
                    }
                }
            } else if (geometry.type === "Polygon") {
                const polygon = geometry as GeoJSON.Polygon;
                for (let c = 0; c < polygon.coordinates.length; c++) {
                    const coordinate = polygon.coordinates[c];
                    for (let i = 0; i < coordinate.length; i++) {
                        coordinate[i] = proj4("WGS84", "EPSG:27700", coordinate[i]);
                        coordinate[i] = [Math.round(coordinate[i][0] * 100) / 100, Math.round(coordinate[i][1] * 100) / 100];
                    }
                }
            } else if (geometry.type === "Point") {
                const point = geometry as GeoJSON.Point;
                point.coordinates = proj4("WGS84", "EPSG:27700", point.coordinates);
                point.coordinates = [Math.round(point.coordinates[0] * 100) / 100, Math.round(point.coordinates[1] * 100) / 100];
            } else if (geometry.type === "LineString") {
                const lineString = geometry as GeoJSON.LineString;
                for (let i = 0; i < lineString.coordinates.length; i++) {
                    lineString.coordinates[i] = proj4("WGS84", "EPSG:27700", lineString.coordinates[i]);
                    lineString.coordinates[i] = [Math.round(lineString.coordinates[i][0] * 100) / 100, Math.round(lineString.coordinates[i][1] * 100) / 100];
                }
            } else if (geometry.type === "MultiPoint") {
                const multiPoint = geometry as GeoJSON.MultiPoint;
                for (let i = 0; i < multiPoint.coordinates.length; i++) {
                    multiPoint.coordinates[i] = proj4("WGS84", "EPSG:27700", multiPoint.coordinates[i]);
                    multiPoint.coordinates[i] = [
                        Math.round(multiPoint.coordinates[i][0] * 100) / 100,
                        Math.round(multiPoint.coordinates[i][1] * 100) / 100,
                    ];
                }
            } else {
                throw new Error("Geometry type not supported yet:" + geometry.type);
            }
        }
        return result;
    }
    public static eastingNorthingToWGS(easting: number, northing: number): [number, number] {
        const neighbourCoordinates = { coordinates: [easting, northing], type: "Point" } as GeoJSON.Point;
        const geo = this.projectToWGS(neighbourCoordinates, ProjectionsRegistrar.EastingNorthingName) as GeoJSON.Point;
        const coordinates = geo.coordinates.reverse();
        return [coordinates[0], coordinates[1]];
    }
    public static projectToWGS(geometry: GeoJSON.Geometry | GeoJSON.FeatureCollection | GeoJSON.Feature, projFrom = "EPSG:27700"): GeoJSON.Geometry | GeoJSON.FeatureCollection {
        const result = cloneDeep(geometry);
        const polygon = result as GeoJSON.Polygon;
        if (polygon.coordinates) {
            this.projGeometryToWGS84(polygon, projFrom);
            return result as GeoJSON.Geometry | GeoJSON.FeatureCollection;
        }
        if ("type" in geometry && geometry.type == "Feature") {
            const feature = geometry as GeoJSON.Feature;
            this.projGeometryToWGS84(feature.geometry, projFrom);
            return feature.geometry as GeoJSON.Geometry;
        } else if ("type" in geometry && geometry.type === "GeometryCollection") {
            const geometryCollection = geometry as GeoJSON.GeometryCollection;
            for (let i = 0; i < geometryCollection.geometries.length; i++) {
                const geometry = geometryCollection.geometries[i];
                this.projGeometryToWGS84(geometry, projFrom);
            }
            return geometryCollection;
        } else {
            const featureCollection = result as GeoJSON.FeatureCollection | undefined;
            if (!featureCollection || !Array.isArray(featureCollection.features)) {
                this.projGeometryToWGS84(result as GeoJSON.Geometry, projFrom);
                return result as GeoJSON.Geometry | GeoJSON.FeatureCollection;
            }
            for (let i = 0; i < featureCollection.features.length; i++) {
                const feature = featureCollection.features[i];
                const geometry = feature.geometry;
                this.projGeometryToWGS84(geometry, projFrom);
            }
            return result as GeoJSON.Geometry | GeoJSON.FeatureCollection;
        }
    }
    private static projGeometryToWGS84(geometry: GeoJSON.Geometry, projFrom: string) {
        if (geometry.type === "MultiPolygon") {
            const polygons = geometry.coordinates;
            for (let p = 0; p < polygons.length; p++) {
                const polygon = polygons[p];
                for (let r = 0; r < polygon.length; r++) {
                    const ring = polygon[r];
                    for (let c = 0; c < ring.length; c++) {
                        ring[c] = proj4(projFrom, "WGS84", ring[c]);
                    }
                }
            }
        } else if (geometry.type === "MultiLineString") {
            const lines = geometry.coordinates;
            for (let l = 0; l < lines.length; l++) {
                const line = lines[l];
                for (let c = 0; c < line.length; c++) {
                    line[c] = proj4(projFrom, "WGS84", line[c]);
                }
            }
        } else if (geometry.type === "LineString") {
            const line = geometry.coordinates;
            for (let c = 0; c < line.length; c++) {
                line[c] = proj4(projFrom, "WGS84", line[c]);
            }
        } else if (geometry.type === "Polygon") {
            const polygon = geometry.coordinates;
            for (let r = 0; r < polygon.length; r++) {
                const ring = polygon[r];
                for (let c = 0; c < ring.length; c++) {
                    ring[c] = proj4(projFrom, "WGS84", ring[c]);
                }
            }
        } else if (geometry.type === "Point") {
            const coords = proj4(projFrom, "WGS84", geometry.coordinates);
            geometry.coordinates = coords;
        } else if (geometry.type === "MultiPoint") {
            const points = geometry.coordinates;
            for (let p = 0; p < points.length; p++) {
                points[p] = proj4(projFrom, "WGS84", points[p]);
            }
        } else {
            throw new Error("Unsupported geometry type: " + geometry.type);
        }
    }
}
